import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gift, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { submitLead } from "../lib/api";
import { FREE_AUDIT_LINK } from "../lib/constants";

const STORAGE_KEY = "rg_audit_popup_dismissed_at";
const COOLDOWN_MS = 24 * 60 * 60 * 1000; // 24 hours
const DELAY_MS = 8000;
const SUPPRESSED_PATHS = ["/get-quote", "/contact", "/quote"];

const initial = { name: "", phone: "", email: "" };

export const LeadPopup = () => {
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(initial);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const { pathname } = useLocation();
    const suppressed = SUPPRESSED_PATHS.includes(pathname);

    useEffect(() => {
        if (suppressed) return undefined;
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const ts = parseInt(saved, 10);
                if (!Number.isNaN(ts) && Date.now() - ts < COOLDOWN_MS) return undefined;
            }
        } catch (_e) {
            // ignore storage errors
        }
        const t = setTimeout(() => setOpen(true), DELAY_MS);
        return () => clearTimeout(t);
    }, [suppressed]);

    useEffect(() => {
        if (suppressed) setOpen(false);
    }, [suppressed]);

    const dismiss = () => {
        try {
            window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
        } catch (_e) {
            // ignore
        }
        setOpen(false);
    };

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") dismiss();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open]);

    const setField = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Please enter your name";
        if (form.phone.replace(/\D/g, "").length < 7) e.phone = "Enter a valid phone";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
            e.email = "Enter a valid email";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            await submitLead({
                name: form.name.trim(),
                phone: form.phone.trim(),
                business_name: "",
                requirement: "Other",
                email: form.email.trim(),
                source: "audit-popup",
                message: "Free Brand Audit request",
            });
            toast.success("Audit request received!", {
                description: "Opening WhatsApp to confirm with you now.",
            });
            window.open(FREE_AUDIT_LINK, "_blank", "noopener,noreferrer");
            dismiss();
            setForm(initial);
        } catch (_err) {
            toast.error("Couldn't send right now", {
                description: "Please try again or call us directly.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    key="popup-backdrop"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="fixed inset-0 z-[120] flex items-center justify-center p-4"
                    data-testid="lead-popup-overlay"
                    role="dialog"
                    aria-modal="true"
                    aria-label="Get a free brand audit"
                >
                    <button
                        type="button"
                        aria-label="Close popup"
                        onClick={dismiss}
                        className="absolute inset-0 bg-black/70 backdrop-blur-md"
                        data-testid="lead-popup-backdrop-click"
                    />
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 16, scale: 0.97 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        className="relative w-full max-w-lg rounded-2xl bg-[#070707] border border-gold/40 shadow-[0_30px_120px_rgba(212,175,55,0.18)] p-7 md:p-9"
                        data-testid="lead-popup-modal"
                    >
                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label="Close"
                            data-testid="lead-popup-close"
                            className="absolute top-4 right-4 w-9 h-9 rounded-full border border-white/10 bg-black/40 hover:bg-black/70 hover:border-gold/40 text-zinc-400 hover:text-gold flex items-center justify-center transition-colors"
                        >
                            <X size={16} />
                        </button>

                        <div className="inline-flex items-center gap-2 rounded-full bg-gold/10 border border-gold/30 px-3 py-1.5 mb-5">
                            <Gift size={13} className="text-gold" />
                            <span className="font-inter text-[10px] uppercase tracking-[0.25em] text-gold">
                                Limited · No obligation
                            </span>
                        </div>

                        <h3
                            data-testid="lead-popup-headline"
                            className="font-poppins font-semibold text-white text-2xl md:text-3xl leading-tight"
                        >
                            Get a <span className="gold-gradient-text">FREE Brand Audit</span> Worth ₹2,000
                        </h3>
                        <p className="mt-3 font-inter text-sm md:text-base text-zinc-400 leading-relaxed">
                            Tell us about your business and we'll review your current branding for free.
                        </p>

                        <form onSubmit={onSubmit} noValidate className="mt-7 space-y-5" data-testid="lead-popup-form">
                            <div>
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    autoComplete="name"
                                    value={form.name}
                                    onChange={(e) => setField("name", e.target.value)}
                                    placeholder="Your full name"
                                    data-testid="lead-popup-input-name"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-2.5 outline-none w-full transition-colors font-inter"
                                />
                                {errors.name && (
                                    <p className="mt-1.5 text-xs text-red-400 font-inter">{errors.name}</p>
                                )}
                            </div>
                            <div>
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                                    Phone Number *
                                </label>
                                <input
                                    type="tel"
                                    autoComplete="tel"
                                    value={form.phone}
                                    onChange={(e) => setField("phone", e.target.value)}
                                    placeholder="+91 98xxx xxxxx"
                                    data-testid="lead-popup-input-phone"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-2.5 outline-none w-full transition-colors font-inter"
                                />
                                {errors.phone && (
                                    <p className="mt-1.5 text-xs text-red-400 font-inter">{errors.phone}</p>
                                )}
                            </div>
                            <div>
                                <label className="font-inter text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2 block">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    autoComplete="email"
                                    value={form.email}
                                    onChange={(e) => setField("email", e.target.value)}
                                    placeholder="you@business.com"
                                    data-testid="lead-popup-input-email"
                                    className="bg-transparent border-b border-white/15 focus:border-gold text-white py-2.5 outline-none w-full transition-colors font-inter"
                                />
                                {errors.email && (
                                    <p className="mt-1.5 text-xs text-red-400 font-inter">{errors.email}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="lead-popup-submit"
                                className="w-full inline-flex items-center justify-center gap-2 bg-gold hover:bg-[#C5A030] disabled:opacity-60 text-black font-medium font-inter px-7 py-3.5 rounded-full transition-all duration-200 hover:shadow-[0_10px_40px_rgba(212,175,55,0.35)]"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        Sending…
                                    </>
                                ) : (
                                    <>
                                        Claim Free Audit
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </button>
                            <p className="text-[11px] font-inter text-zinc-500 text-center">
                                We'll review your branding within 24 hours and share honest feedback.
                            </p>
                        </form>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default LeadPopup;
